import React, { useState, useCallback, useEffect, useMemo } from 'react';
import CookieConsent from 'react-cookie-consent';
import logo from './logo.jpg';
import { supabase } from './lib/supabase.js';
import { Colors as C } from './constants/Colors.js';
import { getAuthRedirectBase } from './constants/siteConfig.js';
import { GLOBAL_CSS } from './constants/musicStyles.js';
import { useMarketplaceHome } from './marketplace/hooks/useMarketplaceHome.js';
import { useSessionStaleBanner } from './marketplace/hooks/useSessionStaleBanner.js';
import { MARKET_SORT_OPTIONS } from './marketplace/marketplaceConstants.js';
import { MarketplaceResultsToolbar } from './marketplace/components/MarketplaceControls.js';
import MarketplaceFiltersDrawerPanel from './marketplace/components/MarketplaceFiltersDrawerPanel.jsx';
import { GridItem, ListaItem } from './marketplace/components/MarketplaceItemCards.js';
import WebTopBar, { getWebTopBarStickyTopPx } from './marketplace/components/WebTopBar.js';
import PageFichaDisc from './marketplace/pages/PageFichaDisc.js';
import PageChat from './marketplace/pages/PageChat.js';
import PagePedido from './marketplace/pages/PagePedido.js';
import PagePerfil from './marketplace/pages/PagePerfil.js';
import PageGroovesCheckout from './marketplace/pages/PageGroovesCheckout.js';
import PageMarketplaceCheckout from './marketplace/pages/PageMarketplaceCheckout.js';
import PageMisVentas from './marketplace/pages/PageMisVentas.js';
import PageAjustes from './marketplace/pages/PageAjustes.js';
import PageNotificaciones from './marketplace/pages/PageNotificaciones.js';
import PageMisSolicitudes from './marketplace/pages/PageMisSolicitudes.js';
import PageComunidad from './marketplace/pages/PageComunidad.js';
import PageComunidadGuardados from './marketplace/pages/PageComunidadGuardados.js';
import PageGroovesRanking from './marketplace/pages/PageGroovesRanking.js';
import PageSubastas from './marketplace/pages/PageSubastas.js';
import PagePublicar from './marketplace/pages/PagePublicar.js';
import PagePerfilVendedor from './marketplace/pages/PagePerfilVendedor.js';
import PageValoraciones from './marketplace/pages/PageValoraciones.js';
import PagePartnerRegistro from './marketplace/pages/PagePartnerRegistro.js';
import PagePartnerPanel from './marketplace/pages/PagePartnerPanel.js';
import PageAdmin from './marketplace/pages/PageAdmin.js';
import PageTiendaEntradas from './marketplace/pages/PageTiendaEntradas.js';
import PageNoticias from './marketplace/pages/PageNoticias.js';
import PageLegal from './marketplace/pages/PageLegal.jsx';
import PageCentroAyuda from './marketplace/pages/PageCentroAyuda.jsx';
import AdSlot from './marketplace/components/AdSlot.jsx';
import MarketplaceFooter from './marketplace/components/MarketplaceFooter.jsx';
import { trackAdClick } from './lib/adsService.js';
import { useFiltersDrawerFab } from './marketplace/context/FiltersDrawerFabContext.js';
import { DCard, BtnGold } from './marketplace/components/MarketplaceAtoms.js';
import PasswordGate from './marketplace/components/PasswordGate.jsx';
import BetaWelcomeSplash from './marketplace/components/BetaWelcomeSplash.jsx';
import { isBetaWelcomePending, markBetaWelcomeSeen } from './marketplace/utils/betaWelcomeStorage.js';
import { isInvitationGateEnabled } from './marketplace/utils/launchPhaseConfig.js';
import {
  isDevInvitationSessionActive,
  isLocalInvitationGateSkipped,
} from './marketplace/utils/invitationGateDev.js';
import { useBreakpoints } from './marketplace/hooks/useBreakpoints.js';
import PasswordInput from './marketplace/components/PasswordInput.jsx';
import { useAppMaintenance } from './marketplace/hooks/useAppMaintenance.js';
import { useLaunchPhase } from './marketplace/hooks/useLaunchPhase.js';
import { useProfile } from './marketplace/hooks/useProfile.js';
import {
  fetchGroovesSpendCosts,
  maxSavedFiltersForProfile,
  purchaseExtraFilterSlotGrooves,
} from './lib/groovesSpendService.js';
import { FILTER_SLOT_EXTRA_COST, GROOVES_PHONE_GATE_USER_MESSAGE, profileGroovesBalance } from './marketplace/constants/groovesEconomy.js';
import { MP_SURF } from './marketplace/constants/surfaces.js';
import { mpPaginationBtnStyle, mpPaginationPageBtnStyle } from './marketplace/constants/paginationStyles.js';

/** Tras este scroll (px), la TopBar pasa a modo compacto (logo pequeño). */
const MP_HEADER_SCROLL_COMPACT_PX = 190;

/** Alto del carrusel hero en el mercado (impacto visual + coherencia con vídeo/portadas). */
const MP_HERO_HEIGHT_PX = 242;

/** Altura aproximada de la barra sticky para backdrop/drawer (alineado con WebTopBar). */
function mpStickyTopPx(compact, mobile) {
  return getWebTopBarStickyTopPx({ compact, mobile, sectionNav: true });
}

function countActiveMarketplaceFilters(f) {
  if (!f) return 0;
  let n = 0;
  if (String(f.busqueda || '').trim()) n += 1;
  if (String(f.provinciaSel || '').trim() || String(f.municipioSel || '').trim()) n += 1;
  if (Array.isArray(f.generosSel) && f.generosSel.length) n += f.generosSel.length;
  if (Array.isArray(f.tiposSel) && f.tiposSel.length) n += f.tiposSel.length;
  if (Array.isArray(f.condFiltros) && f.condFiltros.length) n += f.condFiltros.length;
  if (Array.isArray(f.genFiltros) && f.genFiltros.length) n += f.genFiltros.length;
  if (Array.isArray(f.decadaFiltros) && f.decadaFiltros.length) n += f.decadaFiltros.length;
  if (f.distanciaKmFiltro != null && Number(f.distanciaKmFiltro) > 0) n += 1;
  const pr = f.precioRange;
  const maxUi = Number(f.precioMaxUi);
  const cap = Number.isFinite(maxUi) && maxUi > 0 ? maxUi : 300;
  if (Array.isArray(pr) && pr.length >= 2) {
    const lo = Number(pr[0]) || 0;
    const hi = Number(pr[1]) || 0;
    if (lo > 0 || hi < cap) n += 1;
  }
  if (f.ordenar && f.ordenar !== 'recientes' && f.ordenar !== 'recomendaciones') n += 1;
  return n;
}

// ═══════════════════════════════════
// AUTH MODAL
// ═══════════════════════════════════
const mInput = { width:'100%', padding:'11px 13px', border:'1px solid rgba(0,0,0,0.17)', borderRadius:7, fontSize:'16px', outline:'none', background:'#fafafa', transition:'border-color .18s', fontFamily:'inherit', color:'#1a1a1a' };

function AuthModal({ tab, onTabChange, onClose, onSuccess }) {
  const [email,setEmail]=useState(''); const [password,setPassword]=useState('');
  const [username,setUsername]=useState(''); const [pw2,setPw2]=useState('');
  const [error,setError]=useState(''); const [loading,setLoading]=useState(false);
  const [success,setSuccess]=useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resetPw, setResetPw] = useState('');
  const [resetPw2, setResetPw2] = useState('');
  /** Tras registro: `check_email` = Supabase envió enlace y aún no hay sesión; `ready` = sesión creada al instante (p. ej. sin confirmación en proyecto). */
  const [registerSuccessMode, setRegisterSuccessMode] = useState(null);

  useEffect(() => {
    setSuccess(false);
    setRegisterSuccessMode(null);
    setError('');
    setForgotSent(false);
    setResetPw('');
    setResetPw2('');
  }, [tab]);

  const str = pw => { if(!pw)return{pct:0,color:'transparent',label:''}; if(pw.length<6)return{pct:30,color:C.danger,label:'Débil'}; if(pw.length<8)return{pct:55,color:C.warn,label:'Regular'}; if(pw.length>=8&&/[A-Z]/.test(pw)&&/[0-9]/.test(pw))return{pct:100,color:C.green,label:'Fuerte'}; return{pct:75,color:C.gold,label:'Buena'}; };
  const s = str(password);

  const handleLogin = async e => {
    e.preventDefault(); setError('');
    if(!email.includes('@')){ setError('Correo inválido'); return; }
    if(password.length<6){ setError('Mínimo 6 caracteres'); return; }
    setLoading(true);
    const { error:err } = await supabase.auth.signInWithPassword({ email:email.trim(), password });
    setLoading(false);
    if (err) {
      const raw = (err.message || '').toLowerCase();
      if (err.code === 'email_not_confirmed' || raw.includes('email not confirmed') || raw.includes('not confirmed')) {
        setError('Confirma tu correo: abre el enlace que te enviamos (revisa spam) y luego vuelve a iniciar sesión.');
        return;
      }
      setError(err.message.includes('Invalid') ? 'Correo o contraseña incorrectos' : err.message);
      return;
    }
    onSuccess();
  };

  const handleRegister = async e => {
    e.preventDefault(); setError('');
    if(!username.trim()){ setError('Nombre obligatorio'); return; }
    if(!email.includes('@')){ setError('Correo inválido'); return; }
    if(password.length<6){ setError('Mínimo 6 caracteres'); return; }
    if(password!==pw2){ setError('Las contraseñas no coinciden'); return; }
    setLoading(true);
    const { data, error: err } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: getAuthRedirectBase(),
        data: { username: username.trim() },
      },
    });
    setLoading(false);
    if (err) {
      setError(err.message.includes('already') ? 'Correo ya registrado' : err.message);
      return;
    }
    if (!data?.user) {
      setError('No se pudo crear la cuenta. Inténtalo de nuevo.');
      return;
    }
    if (data.session) {
      await supabase.from('profiles').upsert(
        {
          id: data.user.id,
          username: username.trim(),
          email: email.trim(),
          grooves: 0,
          grooves_total: 0,
          level: 1,
        },
        { onConflict: 'id', ignoreDuplicates: true }
      );
      setRegisterSuccessMode('ready');
    } else {
      setRegisterSuccessMode('check_email');
    }
    setSuccess(true);
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    const { error:err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: getAuthRedirectBase().replace(/\/$/, '') },
    });
    setLoading(false);
    if (err) setError(err.message);
  };

  const handleForgot = async e => {
    e.preventDefault();
    setError('');
    setForgotSent(false);
    const em = email.trim();
    if (!em.includes('@')) {
      setError('Correo inválido');
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(em, {
      redirectTo: getAuthRedirectBase(),
    });
    setLoading(false);
    if (err) {
      setError('No se pudo enviar el correo. Inténtalo de nuevo en unos minutos.');
      return;
    }
    setForgotSent(true);
  };

  const handleResetPassword = async e => {
    e.preventDefault();
    setError('');
    if (resetPw.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (resetPw !== resetPw2) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password: resetPw });
    setLoading(false);
    if (err) {
      setError('No se pudo actualizar la contraseña. Vuelve a abrir el enlace del correo y prueba de nuevo.');
      return;
    }
    setSuccess(true);
    // UX: vuelve a login para que inicie sesión con la nueva contraseña
    setTimeout(() => {
      onTabChange('login');
      setSuccess(false);
      setResetPw('');
      setResetPw2('');
      setPassword('');
    }, 800);
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.48)', zIndex:400, animation:'fadeIn .2s' }}/>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', background:'#fff', borderRadius:14, padding:28, width:'min(400px,93vw)', zIndex:401, boxShadow:'0 20px 50px rgba(0,0,0,0.18)', animation:'slideInDown .22s ease' }}>
        <button onClick={onClose} style={{ position:'absolute', top:12, right:14, background:'none', border:'none', fontSize:'1.1rem', cursor:'pointer', color:'#bbb' }}>✕</button>
        {success ? (
          <div style={{ textAlign:'center', padding:'18px 0' }}>
            <div style={{ fontSize:'2.2rem', marginBottom:10 }}>{registerSuccessMode === 'ready' ? '✓' : '📬'}</div>
            {registerSuccessMode === 'check_email' ? (
              <>
                <h3 style={{ marginBottom:7 }}>Confirma tu correo</h3>
                <p style={{ color:'#888', fontSize:'0.85rem', lineHeight:1.6 }}>
                  Hemos enviado un enlace a <strong>{email}</strong>. Ábrelo para activar la cuenta; hasta entonces el correo no aparecerá como verificado y no podrás iniciar sesión con contraseña.
                </p>
                <p style={{ color:'#aaa', fontSize:'0.72rem', lineHeight:1.5, marginTop:10 }}>
                  Si no ves el mensaje, revisa spam o promociones. El enlace caduca según la política de tu proveedor de correo.
                </p>
                <button type="button" onClick={onClose} style={{ marginTop:18, background:C.gold, color:'#000', border:'none', padding:'10px 26px', borderRadius:7, fontWeight:700, cursor:'pointer' }}>Cerrar</button>
              </>
            ) : (
              <>
                <h3 style={{ marginBottom:7 }}>¡Cuenta lista!</h3>
                <p style={{ color:'#888', fontSize:'0.85rem', lineHeight:1.6 }}>Ya puedes usar DreamVinyl con <strong>{email}</strong></p>
                <button type="button" onClick={() => { onSuccess(); }} style={{ marginTop:18, background:C.gold, color:'#000', border:'none', padding:'10px 26px', borderRadius:7, fontWeight:700, cursor:'pointer' }}>Entrar</button>
              </>
            )}
          </div>
        ) : (
          <>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:18 }}>
              <img src={logo} alt="DreamVinyl" style={{ height:40, borderRadius:7, objectFit:'cover' }}/>
            </div>
            {tab !== 'reset' && (
              <div style={{ display:'flex', background:'rgba(0,0,0,0.05)', borderRadius:8, padding:3, marginBottom:20 }}>
                {[['login','Iniciar sesión'],['register','Crear cuenta']].map(([t,l])=>(
                  <button key={t} onClick={()=>{ onTabChange(t); setError(''); }}
                    style={{ flex:1, padding:'8px', borderRadius:6, border:'none', background:tab===t?'#fff':'transparent', fontWeight:tab===t?700:400, fontSize:'0.82rem', cursor:'pointer', color:tab===t?'#1a1a1a':'#888', transition:'all .13s' }}>
                    {l}
                  </button>
                ))}
              </div>
            )}
            {error && <div style={{ background:'rgba(226,75,74,0.08)', border:`1px solid rgba(226,75,74,0.22)`, borderRadius:6, padding:'9px 11px', color:'#c0392b', fontSize:'0.8rem', marginBottom:13 }}>{error}</div>}

            {tab === 'forgot' && (
              <>
                <h3 style={{ margin:'0 0 10px', fontSize:'1.05rem' }}>Recuperar contraseña</h3>
                <p style={{ margin:'0 0 14px', color:'#888', fontSize:'0.82rem', lineHeight:1.55 }}>
                  Escribe tu correo y te enviaremos un enlace para crear una nueva contraseña.
                </p>
                {forgotSent ? (
                  <div style={{ background:'rgba(29,158,117,0.08)', border:'1px solid rgba(29,158,117,0.22)', borderRadius:8, padding:'10px 12px', color:'#2b7a5b', fontSize:'0.82rem', lineHeight:1.5 }}>
                    Si el correo existe, te hemos enviado un enlace de recuperación. Revisa también spam.
                  </div>
                ) : null}
                <form onSubmit={handleForgot} style={{ display:'flex', flexDirection:'column', gap:11, marginTop:12 }}>
                  <input className="dv-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Tu correo aquí (ej: tu@email.com)" style={mInput} onFocus={e=>e.target.style.borderColor=C.gold} onBlur={e=>e.target.style.borderColor='rgba(0,0,0,0.17)'}/>
                  <button type="submit" style={{ background:C.gold, color:'#000', border:'none', padding:'12px', borderRadius:7, fontWeight:800, fontSize:'0.88rem', cursor:'pointer', opacity:loading ? 0.7 : 1 }} disabled={loading}>
                    {loading ? 'Enviando…' : 'Enviar enlace'}
                  </button>
                </form>
                <button
                  type="button"
                  onClick={() => onTabChange('login')}
                  style={{ marginTop:12, background:'none', border:'none', color:'#888', fontSize:'0.75rem', cursor:'pointer', padding:0 }}
                >
                  Volver a iniciar sesión
                </button>
              </>
            )}

            {tab === 'reset' && (
              <>
                <h3 style={{ margin:'0 0 10px', fontSize:'1.05rem' }}>Nueva contraseña</h3>
                <p style={{ margin:'0 0 14px', color:'#888', fontSize:'0.82rem', lineHeight:1.55 }}>
                  Crea una nueva contraseña para tu cuenta.
                </p>
                <form onSubmit={handleResetPassword} style={{ display:'flex', flexDirection:'column', gap:11 }}>
                  <PasswordInput variant="auth" value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="Nueva contraseña (mínimo 6 caracteres)" autoComplete="new-password" />
                  <PasswordInput variant="auth" value={resetPw2} onChange={e => setResetPw2(e.target.value)} placeholder="Repite la nueva contraseña" autoComplete="new-password" />
                  <button type="submit" style={{ background:C.gold, color:'#000', border:'none', padding:'12px', borderRadius:7, fontWeight:800, fontSize:'0.88rem', cursor:'pointer', opacity:loading ? 0.7 : 1 }} disabled={loading}>
                    {loading ? 'Guardando…' : 'Guardar contraseña'}
                  </button>
                </form>
              </>
            )}

            {tab !== 'forgot' && tab !== 'reset' && (
              <>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={loading}
              style={{
                width:'100%',
                display:'flex',
                alignItems:'center',
                justifyContent:'center',
                gap:10,
                background:'#fff',
                border:'1px solid rgba(0,0,0,0.12)',
                padding:'11px 12px',
                borderRadius:7,
                fontWeight:700,
                fontSize:'0.86rem',
                cursor:loading?'not-allowed':'pointer',
                color:'#1a1a1a',
                boxShadow:'0 8px 20px rgba(0,0,0,0.06)',
                marginBottom:10,
              }}>
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.2 0 5.9 1.1 8.1 3.1l6-6C34.5 3.2 29.7 1 24 1 14.6 1 6.5 6.4 2.7 14.3l7 5.4C11.5 13.6 17.3 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.1 24.5c0-1.5-.1-2.6-.4-3.8H24v7.2h12.7c-.3 1.8-1.8 4.6-5.2 6.4l6.3 4.9c3.7-3.4 8.3-8.4 8.3-14.7z"/>
                <path fill="#FBBC05" d="M9.7 28.3c-.5-1.4-.8-2.9-.8-4.3s.3-2.9.8-4.3l-7-5.4C1 17.7 0 20.8 0 24s1 6.3 2.7 9.7l7-5.4z"/>
                <path fill="#34A853" d="M24 47c5.7 0 10.5-1.9 14-5.1l-6.3-4.9c-1.7 1.2-4 2-7.7 2-6.7 0-12.5-4.1-14.5-10.2l-7 5.4C6.5 41.6 14.6 47 24 47z"/>
              </svg>
              Continuar con Google
            </button>

            <div style={{ display:'flex', alignItems:'center', gap:10, margin:'6px 0 14px' }}>
              <div style={{ flex:1, height:1, background:'rgba(0,0,0,0.08)' }}/>
              <span style={{ fontSize:'0.74rem', color:'#999' }}>o</span>
              <div style={{ flex:1, height:1, background:'rgba(0,0,0,0.08)' }}/>
            </div>

            <form onSubmit={tab==='login'?handleLogin:handleRegister} style={{ display:'flex', flexDirection:'column', gap:11 }}>
              {tab==='register' && <input className="dv-input" value={username} onChange={e=>setUsername(e.target.value)} placeholder="Tu nombre de usuario (ej: vinylcollector_madrid)" style={mInput} onFocus={e=>e.target.style.borderColor=C.gold} onBlur={e=>e.target.style.borderColor='rgba(0,0,0,0.17)'}/>}
              <input className="dv-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Tu correo aquí (ej: tu@email.com)" style={mInput} onFocus={e=>e.target.style.borderColor=C.gold} onBlur={e=>e.target.style.borderColor='rgba(0,0,0,0.17)'}/>
              <div>
                <PasswordInput
                  variant="auth"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={tab === 'register' ? 'Tu contraseña aquí (mínimo 6 caracteres)' : 'Tu contraseña aquí'}
                  autoComplete={tab === 'register' ? 'new-password' : 'current-password'}
                />
                {tab==='register'&&password&&(<div style={{marginTop:5}}><div style={{height:3,background:'rgba(0,0,0,0.07)',borderRadius:2,overflow:'hidden'}}><div style={{height:'100%',width:`${s.pct}%`,background:s.color,transition:'all .3s',borderRadius:2}}/></div><span style={{fontSize:'0.65rem',color:s.color}}>{s.label}</span></div>)}
              </div>
              {tab==='register'&&(
                <PasswordInput
                  variant="auth"
                  value={pw2}
                  onChange={e => setPw2(e.target.value)}
                  placeholder="Repite tu contraseña aquí"
                  autoComplete="new-password"
                />
              )}
              {tab==='login'&&(
                <button
                  type="button"
                  onClick={() => onTabChange('forgot')}
                  style={{background:'none',border:'none',color:'#888',fontSize:'0.72rem',cursor:'pointer',textAlign:'right',padding:0}}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              )}
              <button type="submit" style={{ background:C.gold, color:'#000', border:'none', padding:'12px', borderRadius:7, fontWeight:800, fontSize:'0.88rem', cursor:'pointer', opacity:loading ? 0.7 : 1 }} disabled={loading}>
                {loading?'Cargando...':(tab==='login'?'ENTRAR':'CREAR MI CUENTA')}
              </button>
            </form>
            {tab==='register'&&<p style={{fontSize:'0.68rem',color:'#bbb',textAlign:'center',marginTop:10,lineHeight:1.5}}>Al registrarte aceptas los Términos de uso y la Política de privacidad (RGPD)</p>}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

function LaunchPhaseSectionWall({ sectionLabel, message, phaseLabel, onBack, onGoHome }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0a0a0f',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 440, width: '100%', textAlign: 'center' }}>
        {phaseLabel ? (
          <p style={{ fontSize: 11, letterSpacing: 3, color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>
            {phaseLabel}
          </p>
        ) : null}
        <h1 style={{ fontSize: 22, color: C.gold, marginBottom: 12 }}>
          {sectionLabel} no disponible aún
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.68)', lineHeight: 1.6, marginBottom: 16 }}>
          Estamos abriendo DreamVinyl por fases. Puedes seguir usando el resto de la web con normalidad.
        </p>
        {message ? (
          <DCard style={{ padding: 14, marginBottom: 18, textAlign: 'left', fontSize: 13, color: 'rgba(255,255,255,0.88)' }}>
            <strong style={{ color: C.gold }}>Información</strong>
            <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{message}</div>
          </DCard>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch' }}>
          <BtnGold type="button" onClick={() => onGoHome?.()}>
            Ir al Mercado
          </BtnGold>
          <button
            type="button"
            onClick={() => onBack?.()}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '0.5px solid rgba(255,255,255,0.14)',
              borderRadius: 10,
              padding: '11px 14px',
              color: 'rgba(255,255,255,0.75)',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Volver
          </button>
        </div>
      </div>
    </div>
  );
}

function MaintenanceSectionWall({ sectionLabel, message, finEstimadoText, onBack, onGoHome }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0a0a0f',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 440, width: '100%', textAlign: 'center' }}>
        <h1 style={{ fontSize: 22, color: C.gold, marginBottom: 12 }}>
          {sectionLabel} en mantenimiento
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.68)', lineHeight: 1.6, marginBottom: 16 }}>
          Esta sección no está disponible temporalmente. Vuelve más tarde o explora otras zonas de DreamVinyl.
        </p>
        {message ? (
          <DCard style={{ padding: 14, marginBottom: 18, textAlign: 'left', fontSize: 13, color: 'rgba(255,255,255,0.88)' }}>
            <strong style={{ color: C.gold }}>Mensaje del equipo</strong>
            <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{message}</div>
          </DCard>
        ) : null}
        {finEstimadoText ? (
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 18, lineHeight: 1.5 }}>
            {finEstimadoText}
          </p>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch' }}>
          <BtnGold type="button" onClick={() => onGoHome?.()}>
            Ir al Mercado
          </BtnGold>
          <button
            type="button"
            onClick={() => onBack?.()}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '0.5px solid rgba(255,255,255,0.14)',
              borderRadius: 10,
              padding: '11px 14px',
              color: 'rgba(255,255,255,0.82)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Volver
          </button>
        </div>
      </div>
    </div>
  );
}

function AccountModerationWall({ mode, note, onRecheck }) {
  const blocked = mode === 'blocked';
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0a0a0f',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 440, width: '100%', textAlign: 'center' }}>
        <h1 style={{ fontSize: 22, color: C.gold, marginBottom: 12 }}>
          {blocked ? 'Cuenta suspendida' : 'Cuenta en revisión'}
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.68)', lineHeight: 1.6, marginBottom: 16 }}>
          {blocked
            ? 'Esta cuenta no puede usar DreamVinyl hasta que administración lo restablezca. Si crees que es un error, contacta con soporte.'
            : 'La actividad de esta cuenta está en revisión temporal. Volvé a intentar más tarde o contacta con soporte si necesitás información.'}
        </p>
        {note ? (
          <DCard style={{ padding: 14, marginBottom: 18, textAlign: 'left', fontSize: 13, color: 'rgba(255,255,255,0.88)' }}>
            <strong style={{ color: C.gold }}>Mensaje del equipo</strong>
            <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{note}</div>
          </DCard>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch' }}>
          <BtnGold type="button" onClick={() => void supabase.auth.signOut()}>
            Cerrar sesión
          </BtnGold>
          <button
            type="button"
            onClick={() => void onRecheck?.()}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '0.5px solid rgba(255,255,255,0.14)',
              borderRadius: 10,
              padding: '11px 14px',
              color: 'rgba(255,255,255,0.82)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Comprobar de nuevo
          </button>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', marginTop: 8, lineHeight: 1.45 }}>
            Para consultar avisos legales o ayuda pública, cerrá sesión y abrí esas secciones como visitante.
          </p>
        </div>
      </div>
    </div>
  );
}

// MAIN MARKETPLACE
// ═══════════════════════════════════
export default function Marketplace() {
  const { auth, navigation, ui, filters, list, actions, subastas } = useMarketplaceHome();
  const { user, accountGate, refreshAccountGate, sessionNotice, dismissSessionNotice } = auth;
  useSessionStaleBanner(sessionNotice, dismissSessionNotice);
  const { profile, refetch: refetchProfile } = useProfile(user?.id);
  const maintenance = useAppMaintenance(user?.id);
  const [filterSlotCost, setFilterSlotCost] = useState(FILTER_SLOT_EXTRA_COST);
  const [filterSlotBusy, setFilterSlotBusy] = useState(false);
  const launchPhase = useLaunchPhase();
  const { isMobile, isTablet } = useBreakpoints();
  const {
    page,
    selectedItem,
    chatSeller,
    setChatSeller,
    navigate,
    goBack,
    favoritesOnlyMode,
    ajustesInitialSub,
    clearAjustesInitialSub,
    subastasOpenNuevaSubasta,
    clearSubastasOpenNuevaSubasta,
    pendingDeepLinkNoticiaId,
    clearPendingDeepLinkNoticiaId,
    pendingDeepLinkComunidadId,
    clearPendingDeepLinkComunidadId,
    groovesCheckoutOutcome,
    groovesCheckoutSessionId,
    clearGroovesCheckoutReturn,
    marketplaceCheckoutOutcome,
    marketplaceCheckoutSessionId,
    clearMarketplaceCheckoutReturn,
    pedidoHighlightTransactionId,
    clearPedidoHighlightTransactionId,
  } = navigation;
  const {
    showSplash,
    setShowSplash,
    showModal,
    setShowModal,
    modalTab,
    setModalTab,
    bannerHero,
    setBannerHero,
    heroSlides,
    vista,
    setVista,
    tooltipCond,
    setTooltipCond,
    likes,
  } = ui;
  const { generosSel, ordenar, setOrdenar, currentPage, setCurrentPage } = filters;
  const { allItemsFiltered, auctionItemsFiltered, totalPages, items, gridCols } = list;
  const { closeSplash, limpiar, toggleLike, removeGenero } = actions;

  const invitationGateOn =
    !isLocalInvitationGateSkipped() &&
    !launchPhase.loading &&
    isInvitationGateEnabled(launchPhase.cfg);
  /** null=comprobando cookie; false=pantalla invitación; true=entró */
  const [betaAuthorized, setBetaAuthorized] = useState(null);
  const [showBetaWelcome, setShowBetaWelcome] = useState(false);

  useEffect(() => {
    if (launchPhase.loading) return undefined;
    if (!invitationGateOn) {
      setBetaAuthorized(true);
      return undefined;
    }
    let mounted = true;
    setBetaAuthorized(null);
    const check = async () => {
      if (isDevInvitationSessionActive()) {
        setBetaAuthorized(true);
        return;
      }
      try {
        const res = await fetch('/api/beta-verify', { credentials: 'include', cache: 'no-store' });
        if (!mounted) return;
        if (!res.ok) {
          setBetaAuthorized(false);
          return;
        }
        const data = await res.json().catch(() => ({}));
        setBetaAuthorized(!!data?.authorized);
      } catch (e) {
        if (mounted) setBetaAuthorized(false);
      }
    };
    check();
    return () => {
      mounted = false;
    };
  }, [launchPhase.loading, invitationGateOn]);

  useEffect(() => {
    if (!invitationGateOn) {
      if (isBetaWelcomePending()) setShowBetaWelcome(true);
      return;
    }
    if (betaAuthorized === true && isBetaWelcomePending()) {
      setShowBetaWelcome(true);
    }
  }, [invitationGateOn, betaAuthorized]);

  const dismissBetaWelcome = useCallback(() => {
    markBetaWelcomeSeen();
    setShowBetaWelcome(false);
    closeSplash();
    try {
      window.scrollTo({ top: 0, behavior: 'instant' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  }, [closeSplash]);

  const handleBetaAuthorized = useCallback(() => {
    setBetaAuthorized(true);
    if (isBetaWelcomePending()) setShowBetaWelcome(true);
  }, []);

  // ── REPORTES (UI usuario) ─────────
  const [reportModal, setReportModal] = useState(null); // { kind:'listing', item }
  const [reportCategory, setReportCategory] = useState('inappropriate');
  const [reportBody, setReportBody] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportFlash, setReportFlash] = useState('');
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const { setDrawerOpen: setFiltersDrawerFabOpen } = useFiltersDrawerFab();

  useEffect(() => {
    if (page !== 'marketplace') return undefined;
    setFiltersDrawerFabOpen(filtersDrawerOpen);
    return () => setFiltersDrawerFabOpen(false);
  }, [page, filtersDrawerOpen, setFiltersDrawerFabOpen]);

  const mpFilterCount = useMemo(() => countActiveMarketplaceFilters(filters), [filters]);
  const subastaFilterCount = useMemo(() => countActiveMarketplaceFilters(subastas.filters), [subastas.filters]);

  useEffect(() => {
    if (showSplash) return undefined;
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > MP_HEADER_SCROLL_COMPACT_PX);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [showSplash]);

  useEffect(() => {
    if (!filtersDrawerOpen) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') setFiltersDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [filtersDrawerOpen]);

  useEffect(() => {
    if (!user?.id) return;
    fetchGroovesSpendCosts().then(costs => {
      const c = costs?.filtro_guardado_extra?.cost;
      if (Number.isFinite(c) && c > 0) setFilterSlotCost(c);
    });
  }, [user?.id]);

  const maxSavedFilters = maxSavedFiltersForProfile(profile);
  const filterExtraSlots = Math.max(0, Number(profile?.extra_saved_filter_slots) || 0);
  const canBuyFilterSlot = !!user?.id && !!profile?.phone_verified_at && filterExtraSlots < 3;

  const handlePurchaseFilterSlot = useCallback(async () => {
    if (!canBuyFilterSlot || filterSlotBusy) return;
    if (!profile?.phone_verified_at) {
      window.alert(GROOVES_PHONE_GATE_USER_MESSAGE);
      return;
    }
    const bal = profileGroovesBalance(profile);
    if (bal < filterSlotCost) {
      window.alert(`Necesitas ${filterSlotCost} Grooves para un filtro guardado extra.`);
      return;
    }
    setFilterSlotBusy(true);
    const res = await purchaseExtraFilterSlotGrooves();
    setFilterSlotBusy(false);
    if (!res.ok) {
      window.alert(res.message || 'No se pudo comprar el slot.');
      return;
    }
    await refetchProfile();
  }, [canBuyFilterSlot, filterSlotBusy, profile, filterSlotCost, refetchProfile]);

  const filterSlotPurchase = canBuyFilterSlot
    ? { cost: filterSlotCost, canPurchase: true, busy: filterSlotBusy, onPurchase: handlePurchaseFilterSlot }
    : null;

  /** Invitados: modal para registrar / iniciar sesión */
  const openGuestAuth = useCallback((tab = 'register') => {
    setModalTab(tab);
    setShowModal(true);
  }, [setModalTab, setShowModal]);

  const openReportListing = useCallback((item) => {
    if (!user?.id) {
      openGuestAuth('register');
      return;
    }
    setReportFlash('');
    setReportCategory('inappropriate');
    setReportBody('');
    setReportModal({ kind: 'listing', item });
  }, [user?.id, openGuestAuth]);

  const submitReport = useCallback(async () => {
    if (!user?.id || !reportModal?.kind) return;
    if (reportBusy) return;
    if (String(reportBody || '').trim().length < 6) {
      setReportFlash('Cuéntanos un poco más (mínimo 6 caracteres).');
      return;
    }
    setReportBusy(true);
    setReportFlash('');
    try {
      if (reportModal.kind === 'listing') {
        const listingId = reportModal?.item?.id;
        if (!listingId) throw new Error('listing_missing');
        const { error } = await supabase.from('listing_reports').insert({
          listing_id: listingId,
          reporter_id: user.id,
          category: reportCategory,
          body: String(reportBody || '').trim(),
        });
        if (error) throw error;
      }
        setReportFlash('Reporte recibido correctamente. También te lo enviamos a Notificaciones.');
      setTimeout(() => setReportModal(null), 700);
    } catch (e) {
      setReportFlash('No se pudo enviar el reporte. Inténtalo de nuevo.');
    } finally {
      setReportBusy(false);
    }
  }, [user?.id, reportModal, reportBusy, reportBody, reportCategory]);

  /**
   * Invitado: solo `marketplace` (sin favoritos) y `noticias`.
   * Subastas, comunidad y el resto de rutas → propuesta de registro.
   */
  const navigateWithGuestRules = useCallback(
    (p, arg2) => {
      if (maintenance.isPageBlocked(p)) {
        navigate(p, arg2);
        return;
      }
      if (user && p === 'registro-tienda') {
        const partnerEntry = arg2 && typeof arg2 === 'object' && arg2.partnerEntry === true;
        if (partnerEntry) {
          void (async () => {
            try {
              const { data, error } = await supabase
                .from('partners')
                .select('id, verification_status, is_active')
                .eq('profile_id', user.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
              if (error) throw error;
              if (
                data &&
                data.verification_status === 'approved' &&
                data.is_active !== false
              ) {
                if (launchPhase.partnerOpsEnabled) {
                  navigate('partner-panel');
                } else {
                  navigate('registro-tienda');
                }
                return;
              }
            } catch (_) {
              /* seguir a registro */
            }
            navigate('registro-tienda');
          })();
          return;
        }
        navigate(p, arg2);
        return;
      }
      if (user) {
        navigate(p, arg2);
        return;
      }
      if (p === 'legal') {
        navigate(p, arg2);
        return;
      }
      if (p === 'centro-ayuda') {
        navigate(p, arg2);
        return;
      }
      if (p === 'noticias') {
        navigate(p, arg2);
        return;
      }
      if (p === 'marketplace') {
        if (arg2 && typeof arg2 === 'object' && arg2.favoritesOnly) {
          openGuestAuth('register');
          return;
        }
        navigate(p, arg2);
        return;
      }
      if (p === 'ficha' && arg2 && typeof arg2 === 'object' && arg2.id != null) {
        navigate(p, arg2);
        return;
      }
      if (maintenance.isPageBlocked(p)) {
        navigate(p, arg2);
        return;
      }
      openGuestAuth('register');
    },
    [user, navigate, openGuestAuth, maintenance, launchPhase.partnerOpsEnabled]
  );

  const allowPagesUnderModeration = ['legal', 'centro-ayuda'];
  if (accountGate && user?.id && !allowPagesUnderModeration.includes(page)) {
    return <AccountModerationWall mode={accountGate.mode} note={accountGate.note} onRecheck={refreshAccountGate} />;
  }

  const maintenanceBlock = !maintenance.loading ? maintenance.getBlockedPageMeta(page) : null;
  if (maintenanceBlock && page !== 'admin') {
    return (
      <MaintenanceSectionWall
        sectionLabel={maintenanceBlock.sectionLabel}
        message={maintenanceBlock.message}
        finEstimadoText={maintenanceBlock.finEstimadoText}
        onBack={goBack}
        onGoHome={() => navigate('marketplace')}
      />
    );
  }

  const launchPhaseBlock = !launchPhase.loading ? launchPhase.getBlockedPageMeta(page) : null;
  if (launchPhaseBlock && page !== 'admin') {
    return (
      <LaunchPhaseSectionWall
        sectionLabel={launchPhaseBlock.sectionLabel}
        message={launchPhaseBlock.message}
        phaseLabel={launchPhase.label}
        onBack={goBack}
        onGoHome={() => navigate('marketplace')}
      />
    );
  }

  const webTopBarHiddenNav = [];
  if (!launchPhase.auctionsEnabled) webTopBarHiddenNav.push('subastas');

  if (invitationGateOn) {
    const BETA_EXEMPT_PAGES = ['legal', 'centro-ayuda'];
    if (launchPhase.loading || betaAuthorized === null) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#050507',
            color: 'rgba(255,255,255,0.7)',
            fontSize: 14,
          }}
        >
          Cargando…
        </div>
      );
    }
    if (betaAuthorized === false && !BETA_EXEMPT_PAGES.includes(page)) {
      return <PasswordGate onAuthorized={handleBetaAuthorized} />;
    }
  }

  // ── ROUTING ──────────────────────
  if (page === 'legal')
    return (
      <PageLegal tipo={selectedItem?.legalTipo} onBack={goBack} onNavigate={(p, o) => navigateWithGuestRules(p, o)} user={user} />
    );

  if (page === 'centro-ayuda')
    return <PageCentroAyuda onBack={goBack} onNavigate={(p, o) => navigateWithGuestRules(p, o)} user={user} />;

  if (page === 'ficha' && selectedItem)
    return (
      <PageFichaDisc
        item={selectedItem}
        onBack={goBack}
        onNavigate={p => navigateWithGuestRules(p)}
        liked={!!likes[selectedItem.id]}
        onToggleLike={() => toggleLike(selectedItem.id)}
        onChatSeller={ctx => {
          setChatSeller(ctx);
          navigateWithGuestRules('chat');
        }}
        user={user}
        onRequireAuth={() => {
          setModalTab('login');
          setShowModal(true);
        }}
        paymentsEnabled={launchPhase.paymentsEnabled}
        auctionsEnabled={launchPhase.auctionsEnabled}
        hiddenNavKeys={webTopBarHiddenNav}
      />
    );

  if (page === 'chat')
    return <PageChat onBack={goBack} onNavigate={(p, o) => navigateWithGuestRules(p, o)} user={user} chatContext={chatSeller} />;

  if (page === 'pedido')
    return (
      <PagePedido
        onBack={() => navigateWithGuestRules('perfil')}
        onNavigate={(p, o) => navigateWithGuestRules(p, o)}
        user={user}
        highlightTransactionId={pedidoHighlightTransactionId}
        onConsumedHighlight={clearPedidoHighlightTransactionId}
      />
    );

  if (page === 'grooves-checkout')
    return (
      <PageGroovesCheckout
        outcome={groovesCheckoutOutcome || 'cancel'}
        sessionId={groovesCheckoutSessionId}
        onBack={() => {
          clearGroovesCheckoutReturn();
          goBack();
        }}
        onNavigate={(p, o) => {
          clearGroovesCheckoutReturn();
          navigateWithGuestRules(p, o);
        }}
        user={user}
      />
    );

  if (page === 'marketplace-checkout')
    return (
      <PageMarketplaceCheckout
        outcome={marketplaceCheckoutOutcome || 'cancel'}
        sessionId={marketplaceCheckoutSessionId}
        onBack={() => {
          clearMarketplaceCheckoutReturn();
          goBack();
        }}
        onNavigate={(p, o) => {
          clearMarketplaceCheckoutReturn();
          navigateWithGuestRules(p, o);
        }}
        user={user}
      />
    );

  if (page === 'perfil')
    return (
      <PagePerfil
        onBack={goBack}
        onNavigate={(p, o) => navigateWithGuestRules(p, o)}
        user={user}
        paymentsEnabled={launchPhase.paymentsEnabled}
      />
    );

  if (page === 'misventas')
    return (
      <PageMisVentas
        onBack={() => navigateWithGuestRules('perfil')}
        onNavigate={(p, o) => navigateWithGuestRules(p, o)}
        user={user}
      />
    );

  if (page === 'ajustes')
    return (
      <PageAjustes
        onBack={() => navigateWithGuestRules('perfil')}
        onNavigate={p => navigateWithGuestRules(p)}
        user={user}
        initialSub={ajustesInitialSub}
        onInitialSubConsumed={clearAjustesInitialSub}
      />
    );

  if (page === 'admin')
    return (
      <PageAdmin
        onBack={goBack}
        onNavigate={(p, o) => navigateWithGuestRules(p, o)}
        user={user}
        initialActive={selectedItem?.adminActive ?? null}
        initialComunidadTab={selectedItem?.adminComunidadTab ?? null}
      />
    );

  if (page === 'partner-panel')
    return <PagePartnerPanel onBack={goBack} onNavigate={(p, o) => navigateWithGuestRules(p, o)} user={user} />;

  if (page === 'notificaciones')
    return <PageNotificaciones onBack={goBack} onNavigate={(p, o) => navigateWithGuestRules(p, o)} user={user} />;

  if (page === 'mis-solicitudes')
    return (
      <PageMisSolicitudes
        onBack={() => navigateWithGuestRules('perfil')}
        onNavigate={(p, o) => navigateWithGuestRules(p, o)}
        user={user}
      />
    );

  if (page === 'comunidad')
    return (
      <>
        {showModal && <AuthModal tab={modalTab} onTabChange={setModalTab} onClose={() => setShowModal(false)} onSuccess={() => setShowModal(false)} />}
        <PageComunidad
          onBack={goBack}
          onNavigate={(p, o) => navigateWithGuestRules(p, o)}
          user={user}
          onRequireAuth={tab => { setModalTab(typeof tab === 'string' ? tab : 'login'); setShowModal(true); }}
          deepLinkComunidadId={pendingDeepLinkComunidadId}
          onConsumedDeepLinkComunidad={clearPendingDeepLinkComunidadId}
        />
      </>
    );

  if (page === 'comunidad-guardados')
    return (
      <>
        {showModal && <AuthModal tab={modalTab} onTabChange={setModalTab} onClose={() => setShowModal(false)} onSuccess={() => setShowModal(false)} />}
        <PageComunidadGuardados
          onBack={() => navigateWithGuestRules('perfil')}
          onNavigate={p => navigateWithGuestRules(p)}
          user={user}
          onRequireAuth={tab => { setModalTab(typeof tab === 'string' ? tab : 'login'); setShowModal(true); }}
        />
      </>
    );

  if (page === 'grooves-ranking')
    return (
      <>
        {showModal && <AuthModal tab={modalTab} onTabChange={setModalTab} onClose={() => setShowModal(false)} onSuccess={() => setShowModal(false)} />}
        <PageGroovesRanking
          onBack={goBack}
          onNavigate={(p, o) => navigateWithGuestRules(p, o)}
          user={user}
        />
      </>
    );

  if (page === 'subastas')
    return (
      <>
        {showModal && (
          <AuthModal tab={modalTab} onTabChange={setModalTab} onClose={() => setShowModal(false)} onSuccess={() => setShowModal(false)} />
        )}
        <PageSubastas
          onBack={goBack}
          onNavigate={(p, o) => navigateWithGuestRules(p, o)}
          user={user}
          onRequireAuth={() => {
            setModalTab('login');
            setShowModal(true);
          }}
          totalFiltered={auctionItemsFiltered.length}
          auctionPageItems={subastas.pageItems}
          totalPages={subastas.totalPages}
          currentPage={subastas.currentPage}
          setCurrentPage={subastas.setCurrentPage}
          vista={subastas.vista}
          setVista={subastas.setVista}
          gridCols={subastas.gridCols}
          generosSel={subastas.filters.generosSel}
          removeGenero={subastas.actions.removeGenero}
          ordenar={subastas.filters.ordenar}
          setOrdenar={subastas.filters.setOrdenar}
          likes={likes}
          toggleLike={toggleLike}
          setModalTab={setModalTab}
          setShowModal={setShowModal}
          onClearFilters={subastas.limpiarSubastas}
          filters={subastas.filters}
          filterActions={subastas.actions}
          tooltipCond={subastas.tooltipCond}
          setTooltipCond={subastas.setTooltipCond}
          filterCount={subastaFilterCount}
          openNuevaSubastaRequest={subastasOpenNuevaSubasta}
          onConsumedOpenNuevaSubasta={clearSubastasOpenNuevaSubasta}
        />
      </>
    );

  if (page === 'publicar')
    return (
      <PagePublicar
        onBack={goBack}
        onNavigate={(p, o) => navigateWithGuestRules(p, o)}
        user={user}
        draftFromColeccion={selectedItem?.fromColeccionItem ?? null}
        listingToEdit={selectedItem?.editListing ?? null}
        returnTo={selectedItem?.returnTo ?? null}
        partnerLogisticsEnabled={launchPhase.marketplacePartnerLogisticsEnabled}
      />
    );

  if (page === 'valoraciones')
    return (
      <PageValoraciones
        onBack={goBack}
        onNavigate={(p, o) => navigateWithGuestRules(p, o)}
        user={user}
        itemToRate={selectedItem}
      />
    );

  if (page === 'registro-tienda')
    return (
      <PagePartnerRegistro
        onBack={goBack}
        onNavigate={p => navigateWithGuestRules(p)}
        user={user}
        partnerOpsEnabled={launchPhase.partnerOpsEnabled}
      />
    );

  if (page === 'tienda-entradas')
    return <PageTiendaEntradas onBack={goBack} onNavigate={p=>navigateWithGuestRules(p)} user={user}/>;

  if (page === 'perfil-vendedor' && selectedItem)
    return (
      <PagePerfilVendedor
        seller={selectedItem.seller}
        sellerUserId={selectedItem.sellerUserId ?? null}
        onBack={() => {
          const backPage = selectedItem?.returnTo || 'marketplace';
          navigateWithGuestRules(backPage);
        }}
        onNavigate={(p, o) => navigateWithGuestRules(p, o)}
        onChat={s => {
          setChatSeller(s);
          navigateWithGuestRules('chat');
        }}
        user={user}
        initialAvatarUrl={selectedItem?.sellerAvatarUrl ?? null}
      />
    );

  if (page === 'noticias')
    return (
      <>
        {showModal && (
          <AuthModal tab={modalTab} onTabChange={setModalTab} onClose={() => setShowModal(false)} onSuccess={() => setShowModal(false)} />
        )}
        <PageNoticias
          onBack={goBack}
          onNavigate={p => navigateWithGuestRules(p)}
          onShowModal={tab => {
            setModalTab(typeof tab === 'string' ? tab : 'register');
            setShowModal(true);
          }}
          user={user}
          deepLinkNoticiaId={pendingDeepLinkNoticiaId}
          onConsumedDeepLinkNoticia={clearPendingDeepLinkNoticiaId}
        />
      </>
    );

  // ── MARKETPLACE (home) ────────────────────────────────────────────────────
  return (
    <div style={{ background: MP_SURF.pageBg, minHeight:'100vh', fontFamily:'inherit', color: MP_SURF.text, position:'relative', width:'100%', maxWidth:'100vw', overflowX:'hidden' }}>
      <style>{GLOBAL_CSS}</style>

      {/* ── BIENVENIDA BETA (tras gate, una vez por navegador) ─────────────── */}
      {showBetaWelcome && <BetaWelcomeSplash onDismiss={dismissBetaWelcome} />}

      {/* ── SPLASH ─────────────────────────────────────────────────────────── */}
      {showSplash && !showBetaWelcome && (
        <div
          style={{
            position:'fixed', inset:0, zIndex:1000,
            background:'#050507',
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
            textAlign:'center', padding:20,
            touchAction: 'manipulation',
          }}
        >
          <div className="logo-anim">
            <img src={logo} alt="DreamVinyl" style={{ width:200, borderRadius:22, marginBottom:18, boxShadow:'0 0 50px rgba(200,168,75,0.2)', border:'1px solid rgba(200,168,75,0.25)' }}/>
          </div>
          <h1 style={{ color:'#fff', fontSize:'clamp(1.9rem,5vw,2.8rem)', fontWeight:900, margin:'6px 0', letterSpacing:1 }}>
            VIVE LA <span style={{ color:C.gold }}>MÚSICA</span>
          </h1>
          <p style={{ color:'rgba(255,255,255,0.5)', maxWidth:380, margin:'10px auto 20px', lineHeight:1.6, fontSize:'0.9rem' }}>
            Compra, vende y conecta en el ecosistema definitivo para amantes del formato físico.
          </p>
          <div style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.28)', letterSpacing:4, marginBottom:24 }}>
            VINILOS • CASSETTES • CDs • EVENTOS • OCIO
          </div>
          <button
            type="button"
            className="splash-btn"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              closeSplash();
              try {
                window.scrollTo({ top: 0, behavior: 'instant' });
              } catch (_) {
                window.scrollTo(0, 0);
              }
            }}
            style={{ position: 'relative', zIndex: 1, background:C.gold, color:'#000', border:'none', padding:'15px 26px', borderRadius:4, fontWeight:900, fontSize:'clamp(0.9rem, 3.2vw, 0.95rem)', cursor:'pointer', letterSpacing:1, transition:'all .2s', width:'min(360px, 92vw)' }}
          >
            ENTRAR AL SURCO →
          </button>
          <div style={{ marginTop:10, fontSize:'0.68rem', color:'rgba(255,255,255,0.15)' }}>
            Se cierra automáticamente
          </div>
        </div>
      )}

      {/* ── MODAL AUTH ─────────────────────────────────────────────────────── */}
      {showModal && (
        <AuthModal tab={modalTab} onTabChange={setModalTab} onClose={() => setShowModal(false)} onSuccess={() => setShowModal(false)}/>
      )}

      {/* ── NAV ────────────────────────────────────────────────────────────── */}
      <WebTopBar
        navActive="marketplace"
        user={user}
        onNavigate={(p, opts) => {
          if (p === 'marketplace' && !opts?.favoritesOnly) {
            navigate('marketplace');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            navigateWithGuestRules(p, opts);
          }
        }}
        showWelcomeTab={!showSplash}
        onWelcomeClick={() => { setShowSplash(true); }}
        onShowModal={tab => { setModalTab(tab); setShowModal(true); }}
        onSignOut={goBack}
        notifCount={3}
        headerCompact={headerScrolled}
        dark
        hiddenNavKeys={webTopBarHiddenNav}
      />

      <MarketplaceFiltersDrawerPanel
        open={filtersDrawerOpen}
        onClose={() => setFiltersDrawerOpen(false)}
        onClearFilters={limpiar}
        topPx={mpStickyTopPx(headerScrolled, isMobile)}
        filters={filters}
        actions={actions}
        tooltipCond={tooltipCond}
        setTooltipCond={setTooltipCond}
        maxSavedFilters={maxSavedFilters}
        filterSlotPurchase={filterSlotPurchase}
      />

      <div
        style={{ maxWidth:1400, margin:'0 auto', padding:'0 20px' }}
      >

        {/* Banner hero — ancho completo */}
        <div style={{ margin:'16px 0 13px', borderRadius:12, overflow:'hidden', height:MP_HERO_HEIGHT_PX, position:'relative' }}>
          {heroSlides.map((b,i) => (
            <div
              key={b.id}
              style={{
                position:'absolute',
                inset:0,
                background: b.type === 'video' || b.type === 'image' || b.type === 'ad' ? '#000' : b.bg,
                display:'flex',
                alignItems:'center',
                padding: b.type === 'video' || b.type === 'image' || b.type === 'ad' ? 0 : '0 40px',
                opacity:i===bannerHero?1:0,
                transition:'opacity .6s',
                pointerEvents:i===bannerHero?'auto':'none',
              }}
            >
              {b.type === 'video' ? (
                <video
                  src={b.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{ width: '100%', height: `${MP_HERO_HEIGHT_PX}px`, objectFit: 'cover', objectPosition: 'center center', display: 'block' }}
                />
              ) : b.type === 'image' ? (
                <img
                  src={b.src}
                  alt=""
                  style={{ width: '100%', height: `${MP_HERO_HEIGHT_PX}px`, objectFit: 'cover', objectPosition: 'center center', display: 'block' }}
                />
              ) : b.type === 'ad' ? (
                <div
                  data-guest-safe
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    e.stopPropagation();
                    if (b?.id) trackAdClick(b.id);
                    if (b?.target_url) window.open(b.target_url, '_blank', 'noopener,noreferrer');
                  }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (b?.id) trackAdClick(b.id);
                    if (b?.target_url) window.open(b.target_url, '_blank', 'noopener,noreferrer');
                  }}
                  style={{ width: '100%', height: MP_HERO_HEIGHT_PX, overflow: 'hidden', cursor: 'pointer' }}
                >
                  {String(b.media_type || '').toLowerCase() === 'video' ? (
                    <video
                      src={b.media_url}
                      autoPlay
                      muted
                      loop
                      playsInline
                      style={{ width: '100%', height: `${MP_HERO_HEIGHT_PX}px`, objectFit: 'cover', objectPosition: 'center center', display: 'block' }}
                    />
                  ) : (
                    <img src={b.media_url} alt={b.alt_text || ''} style={{ width: '100%', height: `${MP_HERO_HEIGHT_PX}px`, objectFit: 'cover', objectPosition: 'center center', display: 'block' }} />
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <span style={{ background:b.color, color:'#0b0b10', padding:'4px 10px', borderRadius:6, fontSize:'0.65rem', fontWeight:900, letterSpacing:'0.08em', marginBottom:9, display:'inline-block' }}>{b.tag}</span>
                    <h2 style={{ color:'#fff', fontSize:'clamp(1.25rem, 3.2vw, 1.85rem)', fontWeight:900, margin:'7px 0 4px', fontFamily:"system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif", lineHeight:1.15 }}>{b.title}</h2>
                    <p style={{ color:'rgba(255,255,255,0.78)', fontSize:'0.88rem', lineHeight:1.45 }}>{b.sub}</p>
                  </div>
                </>
              )}
            </div>
          ))}
          <div data-guest-safe style={{ position:'absolute', bottom:10, left:'50%', transform:'translateX(-50%)', display:'flex', gap:6, zIndex:2 }}>
            {heroSlides.map((_,i) => (
              <button
                key={i}
                type="button"
                data-guest-safe
                aria-label={i === bannerHero ? `Slide ${i + 1} (activo)` : `Ir al slide ${i + 1}`}
                onClick={() => setBannerHero(i)}
                style={{
                  width: i === bannerHero ? 18 : 7,
                  height: 7,
                  borderRadius: 4,
                  border: 'none',
                  padding: 0,
                  background: i === bannerHero ? C.gold : 'rgba(255,255,255,0.32)',
                  cursor: 'pointer',
                  transition: 'all .3s',
                }}
              />
            ))}
          </div>
        </div>

        {/* Grid mercado + publicidad lateral (desktop) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile || isTablet ? 'minmax(0,1fr)' : 'minmax(0,1fr) 200px',
            gap: isMobile ? 12 : 18,
            alignItems: 'start',
          }}
        >
          <div style={{ minWidth: 0 }}>
            {favoritesOnlyMode && (
              <div style={{ marginBottom:14, padding:'12px 16px', borderRadius:10, background:'linear-gradient(135deg, rgba(200,168,75,0.14), rgba(83,74,183,0.08))', border:`1px solid rgba(200,168,75,0.35)`, display:'flex', flexWrap:'wrap', alignItems:'center', gap:12, justifyContent:'space-between' }}>
                <div style={{ flex:'1 1 220px', minWidth:0 }}>
                  <div style={{ fontSize:'0.82rem', fontWeight:800, color:C.gold, marginBottom:4 }}>Mis favoritos · interés de compra</div>
                  <div style={{ fontSize:'0.76rem', color:MP_SURF.textMuted, lineHeight:1.45 }}>
                    Solo se listan los anuncios a los que diste al corazón (❤️). Úsalos como lista corta de lo que te gustaría comprar.
                  </div>
                </div>
                <button type="button" onClick={() => navigate('marketplace')}
                  style={{ flexShrink:0, background:'#1a1a2e', color:'#fff', border:'none', padding:'9px 16px', borderRadius:8, fontWeight:700, fontSize:'0.78rem', cursor:'pointer', whiteSpace:'nowrap' }}>
                  Ver todo el mercado
                </button>
              </div>
            )}

            <MarketplaceResultsToolbar
              total={allItemsFiltered.length}
              totalPages={totalPages}
              currentPage={currentPage}
              generosSel={generosSel}
              onRemoveGenero={removeGenero}
              vista={vista}
              onVistaChange={setVista}
              ordenar={ordenar}
              onOrdenarChange={setOrdenar}
              sortOptions={MARKET_SORT_OPTIONS}
              goldColor={C.gold}
              onToggleFilters={() => setFiltersDrawerOpen(o => !o)}
              filtersOpen={filtersDrawerOpen}
              filterCount={mpFilterCount}
              onFeedNovedades={() => { setOrdenar('recientes'); setCurrentPage(1); }}
              onFeedRecomendaciones={() => { setOrdenar('recomendaciones'); setCurrentPage(1); }}
            />

            {items.length===0 ? (
              <div style={{ textAlign:'center', padding:'60px 0', color:MP_SURF.textMuted }}>
                <div style={{ fontSize:'2.4rem', marginBottom:9 }}>{favoritesOnlyMode ? '❤️' : '🔍'}</div>
                {favoritesOnlyMode ? (
                  <>
                    <p style={{ color:MP_SURF.textMuted, maxWidth:360, margin:'0 auto', lineHeight:1.55 }}>Aún no marcas ningún disco con el corazón, o ninguno coincide con los filtros activos.</p>
                    <p style={{ fontSize:'0.78rem', color:MP_SURF.textDim, marginTop:8 }}>Pulsa 🤍 en un anuncio para guardarlo como favorito (interés de compra).</p>
                    <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap', marginTop:16 }}>
                      <button type="button" onClick={() => navigate('marketplace')} style={{ background:C.gold, color:'#000', border:'none', padding:'8px 20px', borderRadius:6, fontWeight:700, cursor:'pointer', fontSize:'0.82rem' }}>Ver todo el mercado</button>
                      <button type="button" onClick={limpiar} style={{ background:'transparent', color:MP_SURF.textMuted, border:`1px solid ${MP_SURF.chipInactiveBorder}`, padding:'8px 20px', borderRadius:6, fontWeight:600, cursor:'pointer', fontSize:'0.82rem' }}>Quitar filtros y favoritos</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p>Sin resultados. Prueba otros filtros.</p>
                    <button type="button" onClick={limpiar} style={{ marginTop:13, background:C.gold, color:'#000', border:'none', padding:'8px 20px', borderRadius:6, fontWeight:700, cursor:'pointer', fontSize:'0.82rem' }}>Limpiar filtros</button>
                  </>
                )}
              </div>
            ) : (
              <>
                <div style={{ display:'grid', gridTemplateColumns:gridCols, gap:vista===1?0:14 }}>
                  {items.map(item => vista===1
                    ? <ListaItem key={item.id} item={item} liked={likes[item.id]} onLike={()=>toggleLike(item.id)} onReport={openReportListing} onClick={()=>navigateWithGuestRules('ficha', item)}/>
                    : <GridItem key={item.id} item={item} liked={likes[item.id]} onLike={()=>toggleLike(item.id)} onReport={openReportListing} onClick={()=>navigateWithGuestRules('ficha', item)}/>
                  )}
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginTop:24, paddingBottom:8 }}>
                    <button type="button" onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={currentPage===1}
                      style={mpPaginationBtnStyle({ active: false, disabled: currentPage === 1 })}>
                      ←
                    </button>
                    {Array.from({length:totalPages},(_,i)=>i+1).map(p=>(
                      <button key={p} type="button" onClick={()=>setCurrentPage(p)}
                        style={mpPaginationPageBtnStyle(p === currentPage)}>
                        {p}
                      </button>
                    ))}
                    <button type="button" onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} disabled={currentPage===totalPages}
                      style={mpPaginationBtnStyle({ active: false, disabled: currentPage === totalPages })}>
                      →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ─── BANNER VERTICAL (solo desktop; en móvil/tablet se evita overflow) ─── */}
          {!(isMobile || isTablet) ? (
            <div style={{ position:'sticky', top: headerScrolled ? 58 : 72, display:'flex', flexDirection:'column', alignSelf:'start', transition: 'top 0.2s ease' }}>
              <div
                style={{
                  width: 200,
                  height: 600,
                  background:'transparent',
                  border:'none',
                  borderRadius:7,
                  padding:0,
                  margin:0,
                  overflow:'hidden',
                }}
              >
                <AdSlot slot="lateral_mercado_subastas" />
              </div>
            </div>
          ) : null}
        </div>

        <MarketplaceFooter
          logoSrc={logo}
          onLegal={t => navigateWithGuestRules('legal', { tipo: t })}
          onNavigate={p => navigateWithGuestRules(p)}
        />

      </div>

      {reportModal?.kind === 'listing' ? (
        <div
          onClick={() => { if (!reportBusy) setReportModal(null); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.62)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 60,
            padding: 14,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 'min(520px, 96vw)',
              background: '#fff',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.12)',
              padding: 16,
              boxShadow: '0 20px 80px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 900, fontSize: '0.98rem', color: '#111' }}>Reportar anuncio</div>
                <div style={{ fontSize: '0.8rem', color: '#777', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {reportModal?.item?.title ? `${reportModal.item.title} · ${reportModal.item.artist || ''}` : 'Anuncio'}
                </div>
              </div>
              <button
                type="button"
                disabled={reportBusy}
                onClick={() => setReportModal(null)}
                style={{ background: 'transparent', border: '1px solid rgba(0,0,0,0.12)', padding: '6px 10px', borderRadius: 8, cursor: reportBusy ? 'default' : 'pointer', color: '#666', fontWeight: 800 }}
              >
                Cerrar
              </button>
            </div>

            <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
              <div style={{ display: 'grid', gap: 6 }}>
                <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 800 }}>Motivo</div>
                <select
                  value={reportCategory}
                  onChange={e => setReportCategory(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid rgba(0,0,0,0.14)', background: '#fafafa', fontWeight: 700 }}
                >
                  <option value="inappropriate">Contenido inapropiado</option>
                  <option value="scam">Posible estafa</option>
                  <option value="spam">Spam</option>
                  <option value="copyright">Derechos de autor</option>
                  <option value="other">Otro</option>
                </select>
              </div>

              <div style={{ display: 'grid', gap: 6 }}>
                <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 800 }}>Detalles</div>
                <textarea
                  value={reportBody}
                  onChange={e => setReportBody(e.target.value)}
                  placeholder="¿Qué ha pasado? (describe el problema para que podamos revisarlo rápido)"
                  rows={4}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid rgba(0,0,0,0.14)', background: '#fafafa', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              {reportFlash ? (
                <div style={{ fontSize: '0.78rem', color: reportFlash.includes('Gracias') ? C.green : C.warn, fontWeight: 800 }}>
                  {reportFlash}
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 2 }}>
                <button
                  type="button"
                  disabled={reportBusy}
                  onClick={() => setReportModal(null)}
                  style={{ background: 'transparent', border: '1px solid rgba(0,0,0,0.12)', padding: '10px 14px', borderRadius: 10, cursor: reportBusy ? 'default' : 'pointer', color: '#666', fontWeight: 800 }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={reportBusy}
                  onClick={() => void submitReport()}
                  style={{ background: C.gold, color: '#000', border: 'none', padding: '10px 14px', borderRadius: 10, cursor: reportBusy ? 'default' : 'pointer', fontWeight: 900 }}
                >
                  {reportBusy ? 'Enviando…' : 'Enviar reporte'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <CookieConsent
        location="bottom"
        buttonText="Aceptar"
        declineButtonText="Solo necesarias"
        enableDeclineButton
        cookieName="dreamvinyl_cookie_consent"
        style={{
          background: '#1a1d28',
          borderTop: '0.5px solid rgba(200,168,75,0.3)',
          fontSize: '12px',
          alignItems: 'center',
          zIndex: 1200,
        }}
        buttonStyle={{
          background: '#C8A84B',
          color: '#000',
          fontSize: '12px',
          borderRadius: '8px',
          padding: '8px 16px',
          fontWeight: '600',
        }}
        declineButtonStyle={{
          background: 'transparent',
          border: '0.5px solid rgba(255,255,255,0.2)',
          color: 'rgba(255,255,255,0.6)',
          fontSize: '12px',
          borderRadius: '8px',
          padding: '8px 16px',
        }}
      >
        Usamos cookies necesarias para el funcionamiento de la plataforma.{' '}
        <button
          type="button"
          style={{ color: '#C8A84B', cursor: 'pointer', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
          onClick={() => navigateWithGuestRules('legal', { tipo: 'cookies' })}
        >
          Política de Cookies
        </button>
      </CookieConsent>
    </div>
  );
}
